import primitiveFactory from "_src/engine/factories/primitive";
import objFactory from "_src/engine/factories/object";
import {primitiveType, stencilAction, stencilFunc, stencilValue} from "_src/engine/constants";

import {mat4, quat, vec3} from "_src/libs/gl-matrix";
import appState from "_src/app_state";
import {photoOrientation, photoState} from "_src/constants";
import output from "_src/engine/output";
import PhotoViewShader from "_src/shaders/photo_view/photo_view";

const SELECTED_OPACITY = 1.0;
const BASE_OPACITY = 0.6;

const vec3tmp = vec3.create();

export default class PhotoGroupWidget {
  constructor(photoGroupObject) {
    this._photos = {};
    this._photoModeWidget = null;
    this.hash = photoGroupObject.getHash();
    for (const [photoID, photo] of photoGroupObject.photos.entries()) {
      this._photos[photoID] = new PhotoWidget(photoGroupObject, photo);
    }
  }

  addToScene(scene) {
    Object.values(this._photos).forEach(photo => photo.addToScene(scene));
  }

  removeFromScene(scene) {
    Object.values(this._photos).forEach(photo => photo.removeFromScene(scene));
  }

  updateVisibility(visible) {
    for (const widget of Object.values(this._photos)) {
      if (widget !== this._photoModeWidget) {
        widget.setVisibility(visible);
      }
    }
  }

  updateState(photoGroupObject) {
    for (const [photoID, photoObject] of photoGroupObject.photos.entries()) {
      const photoWidget = this._photos[photoID];
      if (!photoWidget) continue;

      photoWidget.setState(photoObject.getProperty("state"));
    }
  }

  setTransparentModeEnabled(enabled) {
    if (!this._photoModeWidget) return;

    this._photoModeWidget.setTransparentModeEnabled(enabled);
  }

  enterPhotoMode(cameraControl, photoID, animationTime, isFirstTime) {
    const widget = this._photos[photoID];
    if (!widget) {
      output.error(output.API_ERROR, "Can not view photo. Photo with id " + photoID + " is not exist");
      return;
    }

    if (this._photoModeWidget) this._photoModeWidget.exitPhotoMode();
    widget.setState(photoState.ACTIVE);

    widget.enterPhotoMode(cameraControl, animationTime, isFirstTime);
    this._photoModeWidget = widget;
  }

  exitPhotoMode() {
    if (this._photoModeWidget === null) return;

    this._photoModeWidget.exitPhotoMode();
    this._photoModeWidget = null;
  }

  updateImage(photoID, texture) {
    const widget = this._photos[photoID];
    if (!widget) return;
    widget.updateImage(texture);
  }

  updateUndistortion(texture) {
    Object.values(this._photos).forEach(widget => widget.updateUndistortion(texture));
  }

  resizePhotoMode(cameraControl) {
    if (this._photoModeWidget === null) return;

    this._photoModeWidget.resizePhotoMode(cameraControl);
  }

  castRay(origin, ray) {
    const point = vec3tmp;

    let distance = Infinity;
    let closestWidgetId = null;

    for (const [widgetId, widget] of Object.entries(this._photos)) {
      if (widget.castRay(origin, ray, point)) {

        const dist = vec3.distance(point, origin);

        if (dist < distance) {

          distance = dist;
          closestWidgetId = widgetId;
        }
      }
    }

    return [closestWidgetId, distance];
  }

}

class PhotoWidget {
  constructor(photoGroup, photo) {
    this._2dModel = null;
    this._3dModel = null;
    this._registered = photo.getProperty("registered");
    this._orientation = photo.getProperty("orientation");
    this._transparentModeEnabled = false;
    this._viewModeAnimationPending = false;

    this._init3DModel(photoGroup, photo);
    this._init2DModel(photo);
    if (!this._registered) this._get3DModelDrawable().hide();
  }

  setState(state) {
    switch (state) {
      case photoState.DEFAULT:
        this._get3DModelDrawable().setOpacity(BASE_OPACITY);
        this._get3DModelDrawable().setTransparencyEnabled(true);
        this._get3DModelDrawable().shader.setStencilReferenceValue(stencilValue.DEFAULT_BORDER);
        this._get3DModelDrawable().shader.disablePolygonOffset();
        break;
      case photoState.HOVERED:
        this._get3DModelDrawable().setOpacity(SELECTED_OPACITY);
        this._get3DModelDrawable().setTransparencyEnabled(false);
        this._get3DModelDrawable().shader.setStencilReferenceValue(stencilValue.HOVERED_BORDER);
        this._get3DModelDrawable().shader.enablePolygonOffset();
        this._get3DModelDrawable().shader.setPolygonOffset(-999999, -999999);
        break;
      case photoState.ACTIVE:
        this._get3DModelDrawable().setOpacity(SELECTED_OPACITY);
        this._get3DModelDrawable().setTransparencyEnabled(false);
        this._get3DModelDrawable().shader.setStencilReferenceValue(stencilValue.ACTIVE_BORDER);
        this._get3DModelDrawable().shader.enablePolygonOffset();
        this._get3DModelDrawable().shader.setPolygonOffset(-999999, -999999);
        break;
    }
  }

  setVisibility(visible) {
    if (this._registered) this._get3DModelDrawable().setVisibility(visible);
  }

  setTransparentModeEnabled(enabled) {
    this._transparentModeEnabled = enabled;
    if (this._viewModeAnimationPending) {
      this._2dModel.setVisibility(!enabled);
    } else {
      this._get3DModelDrawable().setVisibility(!enabled);
    }
  }

  addToScene(scene) {
    scene.add(this._3dModel);
    scene.add(this._2dModel);
  }

  removeFromScene(scene) {
    scene.remove(this._3dModel);
    scene.remove(this._2dModel);
  }

  exitPhotoMode() {
    this._get3DModelDrawable().shader.disableScissorTest();
    this._get3DModelDrawable().shader.enableStencilTest();
    this._get3DModelDrawable().shader.enableDepthTest();
    _updateUVOrientation(this._2dModel, this._get3DModelDrawable());

    this._2dModel.hide();
    this._get3DModelDrawable().setVisibility(this._registered);
  }

  enterPhotoMode(cameraControl, animationTime, isFirstTime) {
    this._transparentModeEnabled = false;
    const showInstantly = !isFirstTime || !this._registered;
    const onFinish = () => {
      if (!this._transparentModeEnabled) this._get3DModelDrawable().show();
      if (!showInstantly) this._get3DModelDrawable().shader.setScissor(appState.getStateValue("photoViewRectangleInternal"));
      this._get3DModelDrawable().shader.enableScissorTest();
      this._get3DModelDrawable().shader.disableStencilTest();
      this._get3DModelDrawable().shader.disableDepthTest();
      this._2dModel.hide();
      this._viewModeAnimationPending = false;
    }

    if (showInstantly) {
      this._get3DModelDrawable().hide();
      this._2dModel.show();
    }

    if (!this._registered) {
      this._3dModel.getChildren()[0].setTranslation(...cameraControl.object.trans);
      const rotation = quat.clone(cameraControl.object.quat);
      quat.rotateX(rotation, rotation, Math.PI);
      this._3dModel.getChildren()[0].setRotation(...rotation);
      this._3dModel.updateMatrix();
      this._3dModel.updateBounding();
    }

    this._viewModeAnimationPending = true;
    const screenNDC = cameraControl.moveToPhoto(this._3dModel.getChildren()[0], this._orientation, animationTime, showInstantly, onFinish);
    _adjust2DModel(this._2dModel, screenNDC);
    _updateUVOrientation(this._2dModel, this._get3DModelDrawable(), this._orientation);
    if (showInstantly) this._get3DModelDrawable().shader.setScissor(appState.getStateValue("photoViewRectangleInternal"));
  }

  updateImage(texture) {
    this._2dModel.setTexture(texture, 0);
    this._get3DModelDrawable().setTexture(texture, 0);
  }

  updateUndistortion(texture) {
    this._2dModel.shader.setDirective("USE_UNDISTORTION", true);
    this._2dModel.setTexture(texture, 1);
    this._get3DModelDrawable().shader.setDirective("USE_UNDISTORTION", true);
    this._get3DModelDrawable().setTexture(texture, 1);
  }

  resizePhotoMode(cameraControl) {
    const screenNDC = cameraControl.resize();
    _adjust2DModel(this._2dModel, screenNDC);
    this._get3DModelDrawable().shader.setScissor(appState.getStateValue("photoViewRectangleInternal"));
  }

  castRay(origin, ray, point) {
    return this._get3DModelDrawable().castRay(origin, ray, point);
  }

  _get3DModelDrawable() {
    return this._3dModel.getChildren()[0].getChildren()[0];
  }

  _init3DModel(photoGroup, photo) {
    const shader = new PhotoViewShader({
      transparent: true,
      useCamera: true,
      backFaceCulling: false,
    });
    shader.enableStencilTest();
    shader.setStencilFrontZPassAction(stencilAction.REPLACE);
    shader.setStencilBackZPassAction(stencilAction.REPLACE);
    shader.setStencilFrontZFailAction(stencilAction.KEEP);
    shader.setStencilBackZFailAction(stencilAction.KEEP);
    shader.setStencilReferenceValue(stencilValue.DEFAULT_BORDER);
    shader.setStencilFunction(stencilFunc.ALWAYS);

    const objectSpaceModel = objFactory.createDrawable(shader);
    const photoSpaceModel = objFactory.createDrawable(shader);

    const texture = photo.mip;
    const aspect = texture.height / texture.width;
    const geometryLocalWidth = 2 * photoGroup.getProperty("scenePhotoSize");
    const geometryLocalHeight = geometryLocalWidth * aspect;

    const photoModel = _createPhotoModel(shader, texture, geometryLocalWidth, geometryLocalHeight);

    const {focalDistance, principalPointDelta} = _calcViewModeParams(photoGroup, photo);
    photoModel.setTranslation(2 * principalPointDelta.x, principalPointDelta.y, focalDistance);

    photoSpaceModel.setMatrix(photo.getProperty("transform"));
    objectSpaceModel.setMatrix(photoGroup.getProperty("transform"));

    photoSpaceModel.add(photoModel);
    objectSpaceModel.add(photoSpaceModel);
    objectSpaceModel.updateBounding();

    this._3dModel = objectSpaceModel;
  }

  _init2DModel(photo) {
    const shader = new PhotoViewShader({
      transparent: false,
      useCamera: false,
      backFaceCulling: false,
      depthTest: false,
    });

    this._2dModel = objFactory.createDrawable(shader);
    const texture = photo.mip;

    this._2dModel.addGeometryChunk(primitiveFactory.create(primitiveType.PLANE, {}));
    this._2dModel.setFrustrumCulling(false);
    this._2dModel.setTexture(texture, 0);

    this._2dModel.hide();
  }
}

function _createPhotoModel(shader, texture, width, height) {
  const model = objFactory.createDrawable(shader);

  model.addGeometryChunk(primitiveFactory.create(primitiveType.PLANE, { width, height }));
  model.setFrustrumCulling(false);
  model.setTexture(texture, 0);
  const photoDefaultRotation = quat.create();
  quat.rotateX(photoDefaultRotation, photoDefaultRotation, Math.PI);
  model.rotate(photoDefaultRotation);

  return model;
}

function _adjust2DModel(model, screenNDC) {
  model.setScale(screenNDC[2] - screenNDC[0], screenNDC[3] - screenNDC[1], 1);
  model.setTranslation((screenNDC[2] + screenNDC[0]) / 2, (screenNDC[3] + screenNDC[1]) / 2, 0);
}

function _calcViewModeParams(photoGroup, photo) {
  const fullPhotoDescriptor = photo.getProperty("textures")[0]["levels"][0];
  const tw = fullPhotoDescriptor["width"];
  const th = fullPhotoDescriptor["height"];
  const denormalizationMatrix = mat4.fromValues(tw / 2, 0, 0, 0,
                                                0, th/2, 0, 0,
                                                tw / 2, th/2, 1, 0,
                                                0, 0, 0, 0);
  const tm = photoGroup.getProperty("projection");
  const textureMatrix = mat4.fromValues(tm[0], tm[1], tm[2], 0,
                                        tm[3], tm[4], tm[5], 0,
                                        tm[6], tm[7], tm[8], 0,
                                        tm[9], tm[10], tm[11], 0);
  const denormalizedMatrix = mat4.mul(mat4.create(), denormalizationMatrix, textureMatrix);
  const focalLengthPx = denormalizedMatrix[0];
  const halfHorizontalFovTg = tw / 2 / focalLengthPx;

  const imageWidth = photoGroup.getProperty("scenePhotoSize");
  const focalDistance = imageWidth / halfHorizontalFovTg;

  // principal point in pixels
  const ppx = denormalizedMatrix[8];
  // what part of original image is displaced
  const ppxDeltaPart = (tw / 2 - ppx) / tw;
  // what is displacement of the center of the image from principal point
  const principalPointDelta = {x: 0, y: 0};
  principalPointDelta.x = ppxDeltaPart * imageWidth;

  const ppy = denormalizedMatrix[9];
  const ppyDeltaPart = (th / 2 - ppy) / th;
  // what is displacement of the center of the image from principal point
  const imageAspect = tw / th;
  principalPointDelta.y = ppyDeltaPart * (imageAspect * imageWidth);

  return {focalDistance, principalPointDelta};
}

const _updateUVOrientation = (function() {
  const uvBuffers = new Map();
  uvBuffers.set(photoOrientation.DEFAULT, new Float32Array([0, 1, 1, 1, 0, 0, 1, 0]));
  uvBuffers.set(photoOrientation.ROTATION90, new Float32Array([1, 1, 1, 0, 0, 1, 0, 0]));
  uvBuffers.set(photoOrientation.ROTATION180, new Float32Array([1, 0, 0, 0, 1, 1, 0, 1]));
  uvBuffers.set(photoOrientation.ROTATION270, new Float32Array([0, 0, 0, 1, 1, 0, 1, 1]));
  uvBuffers.set(photoOrientation.MIRROR_HORIZONTAL, new Float32Array([1, 1, 0, 1, 1, 0, 0, 0]));
  uvBuffers.set(photoOrientation.MIRROR_VERTICAL, new Float32Array([0, 0, 1, 0, 0, 1, 1, 1]));
  uvBuffers.set(photoOrientation.ROTATION90_MIRROR_HORIZONTAL, new Float32Array([1, 0, 1, 1, 0, 0, 0, 1]));
  uvBuffers.set(photoOrientation.ROTATION270_MIRROR_HORIZONTAL, new Float32Array([0, 1, 0, 0, 1, 1, 1, 0]));

  return function(model2d, model3d, orientation = photoOrientation.DEFAULT) {
    const uvBuffer = uvBuffers.get(orientation);

    model2d.chunks[0].geometry.getLayoutItem("a_uv").setBuffer(uvBuffer.buffer);
    model2d.chunks[0].markAttributeDirty("a_uv");
    if (orientation === photoOrientation.MIRROR_HORIZONTAL || orientation === photoOrientation.MIRROR_VERTICAL
        || orientation === photoOrientation.ROTATION90_MIRROR_HORIZONTAL ||  orientation === photoOrientation.ROTATION270_MIRROR_HORIZONTAL) {
      const uvBuffer3d = orientation === photoOrientation.MIRROR_HORIZONTAL || orientation === photoOrientation.MIRROR_VERTICAL ?
          uvBuffers.get(photoOrientation.MIRROR_HORIZONTAL) :
          uvBuffers.get(photoOrientation.MIRROR_VERTICAL);
      model3d.chunks[0].geometry.getLayoutItem("a_uv").setBuffer(uvBuffer3d.buffer);
      model3d.chunks[0].markAttributeDirty("a_uv");
    }
  }
})()
